<template>
  <v-container fluid class="pa-6" style="background-color: #121212; min-height: 100vh">
    <v-row class="mb-4 align-center slide-up-item" style="animation-delay: 0ms">
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-btn fab small color="primary" class="mr-4 elevation-10" @click="loadStats" :loading="loading">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn fab small color="grey darken-3" class="mr-4 elevation-10" @click="auditDialog = true" v-bind="attrs" v-on="on">
                <v-icon color="amber">mdi-clipboard-list-outline</v-icon>
              </v-btn>
            </template>
            <span>Transaktions-Liste prüfen</span>
          </v-tooltip>

          <div>
            <h1 class="text-h4 font-weight-black white--text mb-0" style="letter-spacing: -1px">
              Black Friday <span class="primary--text">Mission Control</span>
            </h1>
            <p class="caption grey--text text--lighten-1 mb-0 mt-1">
              <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon>
              Stand: {{ lastUpdated }}
            </p>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="6" class="d-flex justify-end align-center">
        <div class="d-flex align-center px-4 py-2 rounded-xl glass-effect">
          <span class="caption grey--text mr-3 text-uppercase font-weight-bold">Ansicht:</span>
          <v-switch v-model="showActivatedOnly" inset dense hide-details color="success" class="mt-0">
            <template v-slot:label>
              <span class="white--text font-weight-bold" style="font-size: 14px">
                {{ showActivatedOnly ? "Nur Validierte (Done)" : "Alle Verkäufe (Total)" }}
              </span>
            </template>
          </v-switch>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="loading" justify="center" align="center" style="height: 400px">
      <v-progress-circular indeterminate color="primary" size="64" width="6"></v-progress-circular>
    </v-row>

    <div v-else>
      <v-row>
        <v-col cols="12" sm="6" md="4" class="slide-up-item" style="animation-delay: 150ms">
          <v-card class="pa-5 kpi-card card-gradient-blue fill-height" elevation="12" rounded="lg" dark>
            <div class="d-flex justify-space-between align-start">
              <div>
                <div class="text-overline white--text text--lighten-2" style="font-size: 12px; letter-spacing: 1px">
                  {{ showActivatedOnly ? "Validierte Deals" : "Total Verkäufe" }}
                </div>
                <div class="text-h2 font-weight-black white--text mt-2 num-anim">
                  {{ kpiData.totalSales }}
                </div>
              </div>
              <v-icon color="white" size="64" style="opacity: 0.15; transform: rotate(-15deg)">mdi-cart-outline</v-icon>
            </div>
            <v-divider class="my-4" style="opacity: 0.2"></v-divider>
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="caption white--text opacity-70 text-uppercase font-weight-bold">Gesamt Volumen</div>
                <div class="font-weight-bold white--text text-h6">
                  {{ kpiData.totalYears }} <span class="text-body-2 font-weight-regular">Jahre</span>
                </div>
              </div>
              <div class="text-right">
                <div class="caption white--text opacity-70 text-uppercase font-weight-bold">Ø Laufzeit</div>
                <div class="font-weight-bold white--text text-h6">
                  {{ avgYearsPerContract }} <span class="text-body-2 font-weight-regular">Jahre</span>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" class="slide-up-item" style="animation-delay: 200ms">
          <v-card class="pa-5 kpi-card card-gradient-green fill-height" elevation="12" rounded="lg" dark>
            <div class="d-flex justify-space-between align-start">
              <div>
                <div class="text-overline white--text text--lighten-2" style="font-size: 12px; letter-spacing: 1px">Gesamtumsatz</div>
                <div class="text-h5 font-weight-black white--text mt-2 num-anim text-no-wrap">
                  {{ formatMoney(kpiData.totalRevenue) }}
                </div>
              </div>
              <v-icon color="white" size="64" style="opacity: 0.15">mdi-cash-multiple</v-icon>
            </div>
            <v-divider class="my-4" style="opacity: 0.2"></v-divider>
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center">
                <v-icon small color="white" class="mr-2 opacity-70">mdi-chart-line</v-icon>
                <span class="caption white--text opacity-70 text-uppercase font-weight-bold">Durchschnitt / Kunde</span>
              </div>
              <span class="text-h6 white--text font-weight-bold">{{
                kpiData.totalSales > 0 ? formatMoney(kpiData.totalRevenue / kpiData.totalSales) : 0
              }}</span>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="12" md="4" class="slide-up-item" style="animation-delay: 250ms">
          <v-card class="pa-5 kpi-card card-gradient-orange fill-height" elevation="12" rounded="lg" dark>
            <div class="d-flex justify-space-between align-start">
              <div>
                <div class="text-overline white--text text--lighten-2" style="font-size: 12px; letter-spacing: 1px">Backoffice Pipeline</div>
                <div class="d-flex align-baseline mt-2">
                  <div class="text-h3 font-weight-black white--text num-anim mr-2">
                    {{ pipelineData.count }}
                  </div>
                  <div class="text-h6 font-weight-regular white--text opacity-70">Offen</div>
                </div>
              </div>
              <v-icon color="white" size="64" style="opacity: 0.15">mdi-clipboard-alert-outline</v-icon>
            </div>
            <v-divider class="my-4" style="opacity: 0.2"></v-divider>
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="caption white--text opacity-70 text-uppercase font-weight-bold">Offenes Volumen</span>
              <span class="font-weight-bold white--text text-h6 text-no-wrap">{{ formatMoney(pipelineData.revenue) }}</span>
            </div>
            <v-progress-linear :value="pipelineData.percentageDone" color="white" height="6" rounded background-opacity="0.2"></v-progress-linear>
            <div class="text-right caption mt-1 opacity-70 font-weight-bold">{{ Math.round(pipelineData.percentageDone) }}% Validiert</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6" class="slide-up-item" style="animation-delay: 300ms">
          <v-card class="pa-4 kpi-card card-gradient-purple fill-height" elevation="12" rounded="lg" dark>
            <div class="d-flex align-center">
              <v-avatar color="rgba(255,255,255,0.15)" size="56" class="mr-4">
                <v-icon color="white" size="32">mdi-trophy-variant</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="text-overline white--text text--lighten-2" style="font-size: 11px">Top Performance Studio</div>
                <div class="text-h5 font-weight-bold white--text text-truncate">
                  {{ kpiData.topStudio.name }}
                </div>
              </div>
              <div class="text-right">
                <v-chip label small color="white" class="font-weight-black green--text text--darken-3 mb-1">
                  {{ kpiData.topStudio.count }} Deals
                </v-chip>
                <div class="white--text font-weight-bold text-body-1 text-no-wrap">{{ formatMoney(kpiData.topStudio.revenue) }}</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" class="slide-up-item" style="animation-delay: 350ms">
          <v-card class="pa-4 kpi-card card-gradient-dark fill-height" elevation="12" rounded="lg" dark style="border: 1px solid #333">
            <div class="d-flex align-center">
              <v-avatar color="rgba(255,255,255,0.1)" size="56" class="mr-4">
                <v-icon color="orange accent-3" size="28">mdi-timer-sand</v-icon>
              </v-avatar>
              <div class="flex-grow-1 mr-4">
                <div class="text-overline grey--text text--lighten-1" style="font-size: 11px">Kampagnen Status</div>
                <v-progress-linear
                  :value="campaignTimeProgress"
                  color="orange accent-3"
                  height="8"
                  rounded
                  background-color="grey darken-3"></v-progress-linear>
              </div>
              <div class="text-center ml-2">
                <div class="text-h4 font-weight-black white--text">{{ daysLeft }}</div>
                <div class="caption grey--text text-uppercase font-weight-bold">Tage übrig</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-6">
        <v-col cols="12" md="8" class="slide-up-item" style="animation-delay: 400ms">
          <v-card elevation="6" rounded="lg" class="pa-4 fill-height dark-card-bg border-glow">
            <v-card-title class="white--text font-weight-bold pa-0 mb-2">Performance pro Standort</v-card-title>
            <apexchart type="bar" height="450" :options="studioChartOptions" :series="studioChartSeries"></apexchart>
          </v-card>
        </v-col>

        <v-col cols="12" md="4" class="d-flex flex-column">
          <v-card elevation="6" rounded="lg" class="pa-5 mb-6 dark-card-bg flex-grow-1 border-glow slide-up-item" style="animation-delay: 500ms">
            <v-card-title class="white--text font-weight-bold pb-4 pa-0 mb-4">
              <v-icon left color="amber">mdi-podium</v-icon> Laufzeit-Mix
            </v-card-title>
            <div v-for="(duration, index) in durationRanking" :key="index" class="mb-5 group-hover">
              <div class="d-flex justify-space-between align-end mb-1">
                <span class="text-body-1 font-weight-medium grey--text text--lighten-3">{{ duration.name }}</span>
                <div class="text-right">
                  <span class="text-h6 font-weight-black white--text">{{ duration.count }}</span>
                  <span class="caption grey--text ml-1">Stk</span>
                </div>
              </div>
              <v-progress-linear
                :value="duration.percentage"
                :color="getDurationColor(duration.name)"
                height="6"
                rounded
                background-color="grey darken-3"></v-progress-linear>
            </div>
          </v-card>

          <v-card elevation="6" rounded="lg" class="pa-4 dark-card-bg border-glow slide-up-item" style="animation-delay: 600ms">
            <div class="d-flex align-center justify-space-between mb-2">
              <v-card-title class="white--text font-weight-bold pa-0" style="font-size: 1rem">Verteilung</v-card-title>
              <v-btn-toggle v-model="chartMode" mandatory dense rounded class="glass-toggle">
                <v-btn value="status" x-small color="transparent" active-class="primary">Status</v-btn>
                <v-btn value="product" x-small color="transparent" active-class="primary">Angebot</v-btn>
              </v-btn-toggle>
            </div>
            <div class="d-flex align-center justify-center" style="min-height: 200px">
              <apexchart :key="chartMode" type="donut" width="100%" height="250" :options="currentPieOptions" :series="currentPieSeries"></apexchart>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-6 slide-up-item" style="animation-delay: 700ms">
        <v-col cols="12">
          <v-card elevation="6" rounded="lg" class="dark-card-bg border-glow">
            <v-card-title class="white--text">
              Detail-Analyse
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Filter..."
                single-line
                hide-details
                dense
                outlined
                dark
                color="primary"
                class="shrink rounded-lg"
                style="width: 250px"></v-text-field>
            </v-card-title>

            <v-data-table :headers="headers" :items="tableItems" :search="search" hide-default-footer dark class="elevation-0 transparent-table">
              <template v-slot:item.category="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="32" :color="item.category.includes('Upgrade') ? 'amber' : 'primary'" class="mr-3 elevation-2">
                    <span class="white--text font-weight-bold text-caption">{{ item.category.charAt(0) }}</span>
                  </v-avatar>
                  <span class="font-weight-bold text-body-2 white--text">{{ item.category }}</span>
                </div>
              </template>
              <template v-slot:item.sales="{ item }">
                <span class="font-weight-black white--text text-h6">{{ item.sales }}</span>
              </template>
              <template v-slot:item.revenue="{ item }">
                <span style="font-size: 0.7rem !important" class="success--text text--lighten-1 font-weight-bold text-no-wrap">{{
                  formatMoney(item.revenue)
                }}</span>
              </template>
              <template v-slot:item.years="{ item }">
                <span :class="item.years === 0 ? 'grey--text' : 'white--text font-weight-bold'">{{ item.years }}</span>
              </template>
              <template v-slot:item.performance="{ item }">
                <div class="d-flex align-center" style="width: 100%">
                  <v-progress-linear
                    :value="(item.sales / kpiData.totalSales) * 100"
                    height="8"
                    rounded
                    :color="item.category.includes('Upgrade') ? 'amber' : 'success'"
                    background-color="grey darken-3"
                    class="mr-3 flex-grow-1"></v-progress-linear>
                  <span class="caption grey--text text--lighten-1" style="min-width: 35px"
                    >{{ item.sales > 0 ? Math.round((item.sales / kpiData.totalSales) * 100) : 0 }}%</span
                  >
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-dialog v-model="auditDialog" max-width="1000px" scrollable>
      <v-card class="dark-card-bg">
        <v-card-title class="white--text">
          Transaktions-Protokoll (Audit Log)
          <v-spacer></v-spacer>
          <v-btn icon dark @click="auditDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text style="height: 600px">
          <v-data-table
            :headers="auditHeaders"
            :items="currentAuditList"
            :search="auditSearch"
            dark
            class="transparent-table"
            dense
            :items-per-page="15">
            <template v-slot:top>
              <v-text-field v-model="auditSearch" label="Suche (Name, Studio, Produkt)..." dark outlined dense class="mb-4"></v-text-field>
            </template>
            <template v-slot:item.revenue="{ item }">
              <span class="success--text font-weight-bold text-no-wrap">{{ formatMoney(item.revenue) }}</span>
            </template>
            <template v-slot:item.type="{ item }">
              <v-chip x-small :color="item.type === 'Upgrade' ? 'amber' : 'blue'" dark>{{ item.type }}</v-chip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "../../../api"

export default {
  name: "BlackFridayStatistic",
  data: () => ({
    loading: true,
    search: "",
    auditSearch: "",
    auditDialog: false,
    showActivatedOnly: false,
    lastUpdated: "",
    chartMode: "status",

    rawStatsTotal: null,
    rawStatsActivated: null,

    headers: [
      { text: "Angebot", value: "category", align: "start", class: "grey--text text--darken-1" },
      { text: "Anzahl", value: "sales", align: "center", class: "grey--text text--darken-1" },
      { text: "Umsatz", value: "revenue", align: "end", class: " grey--text text--darken-1" },
      { text: "Volumen (Jahre)", value: "years", align: "center", class: "grey--text text--darken-1" },
      { text: "Top Laufzeit", value: "topDuration", align: "center", class: "grey--text text--darken-1" },
      { text: "Anteil", value: "performance", align: "start", width: "300px", class: "grey--text text--darken-1" },
    ],

    auditHeaders: [
      { text: "Typ", value: "type", class: "grey--text" },
      { text: "Kunde", value: "customer", class: "grey--text" },
      { text: "Studio", value: "studio", class: "grey--text" },
      { text: "Produkt", value: "description", class: "grey--text" },
      { text: "Preis (CHF)", value: "revenue", align: "end", class: "grey--text" },
    ],
  }),

  computed: {
    currentDataPayload() {
      return this.showActivatedOnly ? this.rawStatsActivated : this.rawStatsTotal
    },
    currentStats() {
      return this.currentDataPayload ? this.currentDataPayload.stats : null
    },
    currentAuditList() {
      return this.currentDataPayload ? this.currentDataPayload.auditList : []
    },

    // Helper für einfache Summen (Total vs Activated)
    totalSold() {
      return this.calcTotalSales(this.rawStatsTotal?.stats)
    },
    totalSoldRevenue() {
      return this.calcTotalRevenue(this.rawStatsTotal?.stats)
    },
    totalActivated() {
      return this.calcTotalSales(this.rawStatsActivated?.stats)
    },
    totalActivatedRevenue() {
      return this.calcTotalRevenue(this.rawStatsActivated?.stats)
    },

    // NEU: Pipeline Data Calculation
    pipelineData() {
      const openCount = this.totalSold - this.totalActivated
      const openRevenue = this.totalSoldRevenue - this.totalActivatedRevenue
      const percentageDone = this.totalSold > 0 ? (this.totalActivated / this.totalSold) * 100 : 100

      return {
        count: openCount > 0 ? openCount : 0,
        revenue: openRevenue > 0 ? openRevenue : 0,
        percentageDone: percentageDone,
      }
    },

    kpiData() {
      if (!this.currentStats)
        return { totalSales: 0, totalYears: 0, totalRevenue: 0, topCategory: "-", topStudio: { name: "-", count: 0, revenue: 0 } }
      return {
        totalSales: this.calcTotalSales(this.currentStats),
        totalYears: this.calcTotalYears(this.currentStats), // Hier ist das Volumen
        totalRevenue: this.calcTotalRevenue(this.currentStats),
        topCategory: this.calcTopCategory(this.currentStats),
        topStudio: this.calcTopStudio(this.currentStats),
      }
    },

    avgYearsPerContract() {
      if (!this.currentStats) return 0
      let validSales = 0
      let validYears = 0
      Object.entries(this.currentStats).forEach(([key, val]) => {
        if (!key.includes("Upgrade")) {
          validSales += val["gesammt Anzahl"]
          Object.values(val["jahre Pro Studio"] || {}).forEach((v) => (validYears += v))
        }
      })
      return validSales > 0 ? (validYears / validSales).toFixed(1) : 0
    },

    campaignTimeProgress() {
      const start = new Date("2025-11-18T00:00:00")
      const end = new Date("2025-12-07T23:59:59")
      const now = new Date()
      const total = end - start
      const elapsed = now - start
      if (elapsed < 0) return 0
      if (elapsed > total) return 100
      return (elapsed / total) * 100
    },

    daysLeft() {
      const end = new Date("2025-12-07T23:59:59")
      const now = new Date()
      return Math.ceil((end - now) / (1000 * 60 * 60 * 24))
    },

    // Charts & Tables (Simplified logic for brevity, same as before)
    currentPieSeries() {
      return this.chartMode === "status" ? this.memberTypeSeries : this.categoryPieSeries
    },
    currentPieOptions() {
      return {
        theme: { mode: "dark" },
        chart: { background: "transparent" },
        labels: this.chartMode === "status" ? this.memberTypeChartData.labels : this.categoryPieData.labels,
        colors: this.chartMode === "status" ? ["#00E676", "#2979FF", "#B0BEC5"] : ["#FFC107", "#2196F3", "#E91E63", "#00E676"],
        stroke: { show: true, colors: ["#1E1E1E"], width: 3 },
      }
    },
    memberTypeChartData() {
      if (!this.currentStats) return { labels: [], series: [] }
      let types = { Neumitglied: 0, Erneuerung: 0, Ehemalig: 0 }
      Object.values(this.currentStats).forEach((cat) =>
        Object.values(cat.laufzeiten || {}).forEach((d) => {
          types.Neumitglied += d.Neumitglied || 0
          types.Erneuerung += d.Erneuerung || 0
          types.Ehemalig += d.Ehemalig || 0
        })
      )
      return { labels: Object.keys(types), series: Object.values(types) }
    },
    memberTypeSeries() {
      return this.memberTypeChartData.series
    },
    categoryPieData() {
      if (!this.currentStats) return { labels: [], series: [] }
      const labels = []
      const series = []
      Object.entries(this.currentStats).forEach(([key, val]) => {
        labels.push(key.replace("Black Friday ", ""))
        series.push(val["gesammt Anzahl"])
      })
      return { labels, series }
    },
    categoryPieSeries() {
      return this.categoryPieData.series
    },

    studioChartSeries() {
      if (!this.currentStats) return []
      const totals = {}
      Object.values(this.currentStats).forEach((cat) => {
        const studios = cat["anzahl Pro Studio"] || {}
        for (const [s, c] of Object.entries(studios)) totals[s] = (totals[s] || 0) + c
      })
      const data = Object.entries(totals)
        .map(([x, y]) => ({ x, y }))
        .sort((a, b) => b.y - a.y)
      return [{ name: "Verkäufe", data }]
    },
    studioChartOptions() {
      return {
        theme: { mode: "dark" },
        chart: { type: "bar", toolbar: { show: false }, background: "transparent", animations: { enabled: true } },
        plotOptions: { bar: { borderRadius: 6, distributed: true } },
        xaxis: { labels: { style: { colors: "#B0BEC5" } } },
        legend: { show: false },
      }
    },

    durationRanking() {
      if (!this.currentStats) return []
      const counts = {}
      let total = 0
      Object.values(this.currentStats).forEach((cat) =>
        Object.entries(cat.laufzeiten || {}).forEach(([k, d]) => {
          counts[k] = (counts[k] || 0) + d.gesammt
          total += d.gesammt
        })
      )
      return Object.entries(counts)
        .map(([name, count]) => ({ name, count, percentage: total > 0 ? (count / total) * 100 : 0 }))
        .sort((a, b) => b.count - a.count)
    },

    tableItems() {
      if (!this.currentStats) return []
      return Object.entries(this.currentStats).map(([key, val]) => {
        let bestDur = "-"
        let max = 0
        Object.entries(val.laufzeiten || {}).forEach(([dk, dv]) => {
          if (dv.gesammt > max) {
            max = dv.gesammt
            bestDur = dk
          }
        })
        let ySum = 0
        Object.values(val["jahre Pro Studio"] || {}).forEach((v) => (ySum += v))
        return { category: key, sales: val["gesammt Anzahl"], years: ySum, topDuration: bestDur, revenue: val["totalRevenue"] }
      })
    },
  },

  methods: {
    formatMoney(val) {
      return new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 2 }).format(val || 0)
    },
    getDurationColor(n) {
      if (n.includes("3")) return "#FFD700"
      if (n.includes("2")) return "#00E676"
      if (n.includes("1")) return "#2979FF"
      if (n.includes("4")) return "#AA00FF"
      return "grey"
    },
    calcTotalSales(d) {
      if (!d) return 0
      return Object.values(d).reduce((a, c) => a + (c["gesammt Anzahl"] || 0), 0)
    },
    calcTotalYears(d) {
      if (!d) return 0
      let y = 0
      Object.values(d).forEach((c) => Object.values(c["jahre Pro Studio"] || {}).forEach((v) => (y += v)))
      return y
    },
    calcTotalRevenue(d) {
      if (!d) return 0
      return Object.values(d).reduce((a, c) => a + (c["totalRevenue"] || 0), 0)
    },
    calcTopCategory(d) {
      if (!d) return "-"
      let m = 0,
        n = "-"
      for (const [k, v] of Object.entries(d)) {
        if (v["gesammt Anzahl"] > m) {
          m = v["gesammt Anzahl"]
          n = k
        }
      }
      return n.replace("Black Friday ", "")
    },
    calcTopStudio(d) {
      if (!d) return { name: "-", count: 0, revenue: 0 }
      const st = {}
      const sr = {}
      Object.values(d).forEach((c) => {
        for (const [s, v] of Object.entries(c["anzahl Pro Studio"] || {})) st[s] = (st[s] || 0) + v
        for (const [s, v] of Object.entries(c["umsatz Pro Studio"] || {})) sr[s] = (sr[s] || 0) + v
      })
      let m = 0,
        b = "-"
      for (const [s, c] of Object.entries(st)) {
        if (c > m) {
          m = c
          b = s
        }
      }
      return { name: b, count: m, revenue: sr[b] || 0 }
    },
    async loadStats() {
      this.loading = true
      try {
        const [resT, resA] = await Promise.all([api.fetchBlackFridayStats(false), api.fetchBlackFridayStats(true)])
        this.rawStatsTotal = resT.data || resT
        this.rawStatsActivated = resA.data || resA
        this.lastUpdated = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
  },
  created() {
    this.loadStats()
  },
}
</script>

<style lang="scss" scoped>
.dark-card-bg {
  background-color: #1e1e1e !important;
  border: 1px solid #333;
}
.border-glow {
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
}
.kpi-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  overflow: hidden;
  position: relative;
  &:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.5), 0 10px 10px rgba(0, 0, 0, 0.44);
    z-index: 2;
  }
}
.glass-effect {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
}
.glass-toggle {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  ::v-deep .v-btn--active {
    background-color: rgba(33, 150, 243, 0.2) !important;
    color: #2196f3 !important;
    font-weight: bold;
  }
}
.card-gradient-blue {
  background: linear-gradient(135deg, #101c4e 0%, #0d47a1 100%);
}
.card-gradient-purple {
  background: linear-gradient(135deg, #311b92 0%, #6200ea 100%);
}
.card-gradient-green {
  background: linear-gradient(135deg, #004d40 0%, #00695c 100%);
}
.card-gradient-dark {
  background: linear-gradient(135deg, #212121 0%, #424242 100%);
}
/* NEU: Orange Gradient für Pipeline */
.card-gradient-orange {
  background: linear-gradient(135deg, #e65100 0%, #f57c00 100%);
}
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.slide-up-item {
  opacity: 0;
  animation: slideUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}
.group-hover {
  transition: transform 0.2s;
  padding: 4px;
  border-radius: 4px;
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    transform: translateX(4px);
  }
}
.transparent-table {
  background: transparent !important;
  ::v-deep tbody tr {
    transition: background-color 0.2s;
    &:hover {
      background-color: rgba(255, 255, 255, 0.08) !important;
    }
  }
}
.num-anim {
  transition: all 0.3s ease;
}
</style>
